import React, { useEffect, useState } from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { compose } from 'recompose';
import { handleInitTokens } from 'src/store/authentication/authentication.actions';
import { withRouter } from 'react-router-dom';
import { redirectToLogin } from 'src/session';
import { requestAccount } from 'src/store/account/account.requests';
import { LOCAL_STORAGE_USER_AUTH_SESSION, authenticatedUser, authentication } from 'src/utilities/storage';
import { equals, head, pathOr } from 'ramda';
import { ApplicationState } from 'src/store/ApplicationState';
import { PASSWORD_UPDATE_ROUTE, START_APPLICATION_AUTH, START_APPLICATION_HOME } from 'src/route_constants';
import { isAccountPasswordExpired } from 'src/services/objects/user';
import { COOKIE } from 'src/services/auth/auth';
import { isNotEmpty } from 'src/utilities/hasValue';
import SiteSetting from 'src/siteSetting';
import { buildLabel } from 'src/utilities/naming';
import { META_DATA } from 'src/services/constants';

interface Props {
  location: {
    pathname: string;
    search: string;
  };
  history: {
    push: Function;
  };
  children: any;
}

type CombinedProps = DispatchProps & StateProps & Props;

const AuthenticationWrapper: React.FC<CombinedProps> = (
  props: CombinedProps
) => {
  const [showChildren, setShowChildren] = useState(false);

  const token = authentication.token.get();
  const accountId = authenticatedUser.accountId.get();

  useEffect(() => {
    const {
      actions: { initSession },
    } = props;
    if (
      window.location?.pathname?.match(/\/loans\/manage\/applications/)
    ) {
      window.location.assign(START_APPLICATION_HOME);
    }

    if (
      window.location?.pathname?.match(/\/loans\/manage\/users/)
    ) {
      window.location.assign(START_APPLICATION_AUTH);
    }

    /**
     * set redux state to what's in local storage
     * or expire the tokens if the expiry time is in the past
     *
     * if nothing exists in local storage, we get shot off to login
     */
    initSession();

    const handleAuthenticationChange = (event: StorageEvent) => {
      if (equals(pathOr('', ['key'], event), LOCAL_STORAGE_USER_AUTH_SESSION)) {
        redirectToLogin('', '');
      }
    }

    window.addEventListener("storage", handleAuthenticationChange)


    return () => {
      window.removeEventListener("storage", handleAuthenticationChange)
    }
    /**
     * this is the case where we've just come back from login and need
     * to show the children onMount
     */
  }, []);

  /**
   * We make a series of requests for data on app load. The flow is:
   * 1. App begins load; users see splash screen
   * 2. Initial requests (in makeInitialRequests) are made (account, profile, etc.)
   * 3. Initial requests complete; app is marked as done loading
   * 4. As splash screen goes away, secondary requests (in makeSecondaryRequests -- Lendsmarts, types, regions)
   * are kicked off
   */
  const makeInitialRequests = async () => {
    // When loading Lish we avoid all this extra data loading
    if (window.location?.pathname?.match(/logout/)) {
      return;
    }
    const {
      actions: { requestAccount },
    } = props;
    const accountId = authenticatedUser.accountId.get();
    // Initial Requests: Things we need immediately (before rendering the app)
    const dataFetchingPromises: Promise<any>[] = [
      requestAccount(accountId),
      // Grants/what a user has permission to view
      // Is a user managed
    ];

    try {
      return await Promise.all(dataFetchingPromises);
    } catch {
      /** We choose to do nothing, relying on the Redux error state. */
    }
    return
  };


  const redirectToExpiryRoute = (accounts: any) => {
    if (isAccountPasswordExpired(accounts)) {
      return props.history.push(PASSWORD_UPDATE_ROUTE, accounts)
    }
    return ''
  }

  /**
   * handles for the case where we've refreshed the page
   * and redux has now been synced with what is in local storage
   */
  const hasAuth = isNotEmpty(token) || (isNotEmpty(accountId) && equals(SiteSetting.getCoreAPIAuthProvider(), COOKIE))

  useEffect(() => {
    (async () => {
      if (hasAuth && !showChildren) {
        const accounts: any = await makeInitialRequests();
        const account = head(accounts)
        authenticatedUser.accountTimeZone.set(pathOr('',[META_DATA, buildLabel('time_zone')],account))
        redirectToExpiryRoute(account)
        setShowChildren(true);
      }
    })()

    if (!hasAuth) {
      setShowChildren(false);
      redirectToLogin(location.pathname, location.search);
    }
  }, [hasAuth]);

  return (
    <React.Fragment>{showChildren ? props.children : null}</React.Fragment>
  );
};

interface StateProps {
  authenticated: any;
}

const mapStateToProps: MapStateToProps<StateProps, {}, ApplicationState> = (
  state
) => ({
  authenticated: pathOr('', ['authentication'], state),
});

interface DispatchProps {
  actions: any;
}
const mapDispatchToProps: MapDispatchToProps<DispatchProps, {}> = (
  dispatch: any,
  _: any
) => {
  return {
    actions: {
      initSession: () => dispatch(handleInitTokens()),
      requestAccount: (id: any) => dispatch(requestAccount(id)),
    },
  };
};

const connected = connect(mapStateToProps, mapDispatchToProps);

export default compose(connected, withRouter)(AuthenticationWrapper);
